import React, { Suspense, ComponentType, ReactNode } from 'react';
import { Spin, Result, Button } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import ErrorBoundary from './ErrorBoundary';

interface LazyWrapperProps {
    children: ReactNode;
    fallback?: ReactNode;
    errorFallback?: ComponentType<{ error: Error; resetError: () => void }>;
}

interface LazyLoadErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    retryCount: number;
}

// Enhanced loading fallback
const DefaultLoadingFallback: React.FC<{ message?: string }> = ({
    message = 'Loading component...'
}) => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        flexDirection: 'column'
    }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>{message}</p>
    </div>
);

// Enhanced error fallback for lazy loading failures
const DefaultLazyErrorFallback: React.FC<{
    error: Error;
    resetError: () => void;
    retryCount?: number;
}> = ({ error, resetError, retryCount = 0 }) => {
    const isChunkError = error.message.includes('Loading chunk') ||
        error.message.includes('ChunkLoadError') ||
        error.name === 'ChunkLoadError';

    const handleRetry = () => {
        if (isChunkError && retryCount < 3) {
            // For chunk errors, try to reload the page after a short delay
            setTimeout(() => {
                window.location.reload();
            }, 500);
        } else {
            // Clear any cached modules and retry
            if ('caches' in window) {
                caches.keys().then(cacheNames => {
                    cacheNames.forEach(cacheName => {
                        if (cacheName.includes('vite') || cacheName.includes('workbox')) {
                            caches.delete(cacheName);
                        }
                    });
                });
            }
            resetError();
        }
    };

    const handleGoHome = () => {
        window.location.href = '/';
    };

    return (
        <div style={{ padding: '20px' }}>
            <Result
                status="error"
                title={isChunkError ? "Failed to Load Component" : "Component Error"}
                subTitle={
                    isChunkError
                        ? "The component failed to load. This might be due to a network issue or an updated version of the app."
                        : "An error occurred while loading this component."
                }
                extra={[
                    <Button
                        type="primary"
                        key="retry"
                        icon={<ReloadOutlined />}
                        onClick={handleRetry}
                        loading={retryCount >= 3}
                    >
                        {isChunkError && retryCount < 3 ? 'Reload Page' : 'Try Again'}
                    </Button>,
                    <Button
                        key="home"
                        icon={<HomeOutlined />}
                        onClick={handleGoHome}
                    >
                        Go Home
                    </Button>
                ]}
            >
                {import.meta.env.DEV && (
                    <div style={{
                        marginTop: '20px',
                        padding: '16px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        textAlign: 'left'
                    }}>
                        <h4>Error Details (Development Mode):</h4>
                        <pre style={{
                            fontSize: '12px',
                            color: '#d32f2f',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {error.toString()}
                        </pre>
                        <p style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                            Retry count: {retryCount}
                        </p>
                    </div>
                )}
            </Result>
        </div>
    );
};

// Error boundary specifically for lazy loading
class LazyLoadErrorBoundary extends React.Component<
    {
        children: ReactNode;
        fallback?: ComponentType<{ error: Error; resetError: () => void }>;
    },
    LazyLoadErrorBoundaryState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            retryCount: 0
        };
    }

    static getDerivedStateFromError(error: Error): Partial<LazyLoadErrorBoundaryState> {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('🚨 LazyLoadErrorBoundary - Caught lazy loading error:', error);
        console.error('🚨 LazyLoadErrorBoundary - Error info:', errorInfo);

        // Log specific chunk loading errors
        if (error.message.includes('Loading chunk') || error.name === 'ChunkLoadError') {
            console.error('🚨 LazyLoadErrorBoundary - Chunk loading failed, this might be due to:');
            console.error('  - Network connectivity issues');
            console.error('  - App was updated and old chunks are no longer available');
            console.error('  - CDN or server issues');
        }
    }

    resetError = () => {
        this.setState(prevState => ({
            hasError: false,
            error: null,
            retryCount: prevState.retryCount + 1
        }));
    };

    render() {
        if (this.state.hasError && this.state.error) {
            const FallbackComponent = this.props.fallback || DefaultLazyErrorFallback;
            return (
                <FallbackComponent
                    error={this.state.error}
                    resetError={this.resetError}
                />
            );
        }

        return this.props.children;
    }
}

// Main LazyWrapper component
export const LazyWrapper: React.FC<LazyWrapperProps> = ({
    children,
    fallback,
    errorFallback
}) => {
    return (
        <LazyLoadErrorBoundary fallback={errorFallback}>
            <Suspense fallback={fallback || <DefaultLoadingFallback />}>
                {children}
            </Suspense>
        </LazyLoadErrorBoundary>
    );
};

// Higher-order component for creating lazy components with enhanced error handling
export const createLazyComponent = <T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    loadingMessage?: string,
    retryAttempts = 3
) => {
    // Create a wrapper for the import function with retry logic
    const importWithRetry = () => {
        return new Promise<{ default: T }>((resolve, reject) => {
            let attempts = 0;
            
            const attemptImport = async (): Promise<void> => {
                try {
                    const module = await importFn();
                    resolve(module);
                } catch (error) {
                    attempts++;
                    console.warn(`Attempt ${attempts} failed for lazy import:`, error);
                    
                    if (attempts < retryAttempts) {
                        // Wait with exponential backoff before retry
                        const delay = Math.min(1000 * Math.pow(2, attempts - 1), 5000);
                        console.log(`Retrying import in ${delay}ms...`);
                        setTimeout(attemptImport, delay);
                    } else {
                        console.error(`All ${retryAttempts} import attempts failed:`, error);
                        reject(error);
                    }
                }
            };
            
            attemptImport();
        });
    };
    
    const LazyComponent = React.lazy(importWithRetry);

    // Return a simple function component to avoid ref typing issues with React.lazy
    return (props: React.ComponentProps<T>) => (
        <LazyWrapper fallback={<DefaultLoadingFallback message={loadingMessage} />}>
            <LazyComponent {...(props as any)} />
        </LazyWrapper>
    );
};

// Utility function to preload lazy components
export const preloadLazyComponent = (importFn: () => Promise<any>) => {
    return importFn().catch(error => {
        console.warn('Failed to preload component:', error);
    });
};

export default LazyWrapper;